import type { ReactNode } from 'react';
import { ImagePlus, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * The couple's portrait as a control: a button around the picture, a scrim with
 * an icon on hover and focus, and a filled badge pinned to the circle's edge.
 *
 * The badge is what carries the affordance. A scrim that only appears on hover
 * says nothing on a touch screen, and nothing at all to a planner who never
 * happens to put the pointer there — a circle that reads as an avatar is not a
 * control until something on it says otherwise. The scrim then confirms it for
 * whoever does hover, and `focus-visible` gives the keyboard the same signal.
 *
 * Two sizes rather than one set of numbers used at two diameters: a badge tuned
 * to read as a detail on the create dialog's 96px circle reads as a sticker on
 * the hub header's 64px one. The offsets below are not corner insets — they put
 * the badge on the circle's edge at 45°, which depends on the diameter and the
 * ring padding both.
 */
const GEOMETRY = {
  /** The create dialog: 96px outer, 6px of ring padding, so the 84px picture's
   *  lower-right point sits 4px in from both edges. */
  lg: {
    badge: 'size-7 right-1 bottom-1',
    badgeIcon: 'size-3.5',
    icon: 'size-6',
  },
  /** The hub header: 64px, 80px from `sm` up. The picture is 52px and 68px, so
   *  the 45° point moves from ~2px to ~4px in as the circle grows. */
  sm: {
    badge: 'size-6 right-0.5 bottom-0.5 sm:right-1 sm:bottom-1',
    badgeIcon: 'size-3',
    icon: 'size-5',
  },
} as const;

export default function CouplePhotoTarget({
  size,
  label,
  onClick,
  busy = false,
  className,
  children,
}: {
  size: keyof typeof GEOMETRY;
  /** The accessible name. Names the action, not the picture — the photo itself
   *  is decorative wherever a heading beside it already names the couple. */
  label: string;
  onClick: () => void;
  busy?: boolean;
  /** The circle's width, from the caller that owns the surrounding layout. */
  className?: string;
  /** The `CouplePhoto` this wraps. */
  children: ReactNode;
}) {
  const g = GEOMETRY[size];

  return (
    <div className={cn('relative', className)}>
      <button
        type="button"
        aria-label={label}
        aria-busy={busy || undefined}
        disabled={busy}
        onClick={onClick}
        className="group block w-full cursor-pointer rounded-full transition-transform focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:outline-none active:scale-[0.98] disabled:cursor-default disabled:active:scale-100"
      >
        {children}
        <span
          className={cn(
            'pointer-events-none absolute inset-1.5 flex items-center justify-center rounded-full bg-foreground/45 transition-opacity duration-150',
            // While a save is in flight the scrim stays up and holds the
            // spinner: the picture underneath is still the old one, and dimming
            // it is what says so.
            busy
              ? 'opacity-100'
              : 'opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100',
          )}
        >
          {busy ? (
            <Loader2 className={cn(g.icon, 'animate-spin text-background')} />
          ) : (
            <ImagePlus className={cn(g.icon, 'text-background')} />
          )}
        </span>
      </button>
      <span
        className={cn(
          'pointer-events-none absolute flex items-center justify-center rounded-full bg-primary text-primary-foreground ring-2 ring-background',
          g.badge,
        )}
      >
        <ImagePlus className={g.badgeIcon} />
      </span>
    </div>
  );
}
